import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import { useNavigate } from 'react-router-dom';
import useToast from '../../hooks/useToast';
import api from '../../api/api';
import Toast from '../Toast';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const CouponModal = ({ isCouponModalOpen, onClose, cartItems, onCouponApplied }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  useBodyScrollLock(isCouponModalOpen);
  const [inputValue, setInputValue] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { toastMessage, toastType, showToast } = useToast();
  const { idToken, logout } = useUser();
  const { clearCart } = useCart();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isCouponModalOpen) {
      const timer = setTimeout(() => setShow(true), 10);
      return () => clearTimeout(timer);
    }

    setShow(false);
    setInputValue('');
    setCoupon(null);
    setHasSearched(false);
  }, [isCouponModalOpen]);

  useEffect(() => {
    if (show) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [show]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleSearchCoupon = async () => {
    if (!inputValue.trim()) {
      showToast(t('Please enter a coupon code'), 'warning');
      return;
    }

    try {
      setIsLoading(true);
      setHasSearched(true);
      
      const response = await api.get('/buyer/coupon/search', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        params: { code: inputValue.trim() },
      });

      const coupon = response.data?.response?.data;
      
      if (coupon) {
        setCoupon(coupon);
      } else {
        setCoupon(null);
        showToast(t('Coupon not found'), 'warning');
      }
    } catch (error) {
      setCoupon(null);
      log.error('Failed to search coupon:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleApplyCoupon = async (couponToApply) => {
    if (!couponToApply?.code) {
      showToast(t('Please enter a coupon code'), 'warning');
      return;
    }

    try {
      setIsApplyingCoupon(true);

      const response = await api.post(
        '/buyer/coupon/apply',
        {
          code: couponToApply.code,
          cartItems: (cartItems || []).map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            frVariantName: item.frVariantName,
            enVariantName: item.enVariantName,
            variantFrTitle: item.variantFrTitle,
            variantEnTitle: item.variantEnTitle,
            quantity: item.quantity,
            price: item.price,
            merchantConfigId: item.merchantConfigId,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      const { success, coupon, discountAmount, discountedItems } = response.data?.response?.data;

      if (success) {
        onCouponApplied?.({
          coupon,
          discountAmount,
          discountedItems,
        });
        showToast(t('Coupon applied', { code: coupon.code }), 'success');
        onClose();
      } else {
        showToast(t('Failed to apply coupon'), 'error');
      }
    } 
    catch (error) {
      log.error('Failed to apply coupon:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchCoupon();
    }
  };

  const getScopeText = (coupon) => {
    if (!coupon.scope) {
      return null;
    }

    switch (coupon.scope.scopeType) {
    case 'website':
      return t('Applies to the entire website');

    case 'store':
      return t('Stores: {{stores}}', {
        stores: coupon.scope.selectedItems.map((s) => s.storeName).join(', '),
      });

    case 'category':
      return t('Categories: {{categories}}', {
        categories: i18n.language === 'fr'
          ? coupon.scope.selectedItems.map((c) => c.frName).join(', ')
          : coupon.scope.selectedItems.map((c) => c.enName).join(', '),
      });

    case 'product':
      return t('Products: {{products}}', {
        products: i18n.language === 'fr'
          ? coupon.scope.selectedItems.map((p) => p.frName).join(', ')
          : coupon.scope.selectedItems.map((p) => p.enName).join(', '),
      });

    case 'customer':
      return t('Customer-specific coupon');

    default:
      return null;
    }
  };

  return (
    <div>
      <div
        className={`fixed bottom-0 lg:top-0 right-0 px-4 py-8 left-auto w-full lg:w-[550px] max-h-[85dvh] lg:max-h-full lg:h-full bg-[#f3f3f4] z-50 overflow-y-auto transition-transform duration-300 ${show ? 'modalanimatedRight' : 'whennotmodalcloseRight'}`}
      >
        <div className="flex items-center border border-gray-400 bg-[#fcfcfc] rounded px-4 h-11">
          <input
            ref={inputRef}
            type="text"
            placeholder={t('Enter coupon here')}
            className="flex-1 py-1 border-none outline-none text-xs bg-transparent placeholder:text-[#9DA0A7] uppercase"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button
            onClick={handleSearchCoupon}
            disabled={isLoading || !inputValue.trim()}
            className={`text-sm uppercase font-medium transition-colors ${
              inputValue.trim() && !isLoading ? 'text-[#1E88E5] cursor-pointer' : 'text-[#9DA0A7] cursor-not-allowed'
            }`}
          >
            {isLoading ? t('Searching...') : t('search')}
          </button>
        </div>

        {hasSearched && (
          <>
            <hr className="h-[2px] bg-gray-400 w-full mt-10" />
            <h3 className="text-lg font-semibold text-center bg-[#f3f3f4] px-3 -mt-4 w-fit mx-auto">
              {t('Search Result')}
            </h3>

            <div className="mt-6">
              {isLoading ? (
                <div className="text-center text-sm text-[#9DA0A7] py-8">
                  {t('Searching...')}
                </div>
              ) : coupon ? (
                <div className="border border-gray-400 p-4 rounded">
                  {/* Top row: name + apply button */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {coupon.name && (
                        <p className="text-base font-semibold text-[#000000DE]">
                          {coupon.name}
                        </p>
                      )}

                      <p className="text-sm text-[#777]">{coupon.code}</p>
                      <p className="text-sm font-semibold text-[#31c214] mt-1">
                        {coupon.couponType.type === 'percentage'
                          ? t('{{percentage}}% OFF', { percentage: coupon.couponType.percentage })
                          : coupon.couponType.type === 'flat-amount'
                            ? t('{{amount}}$ OFF', { amount: coupon.couponType.amount })
                            : t('Free Delivery')}
                      </p>
                    </div>

                    <button
                      onClick={() => handleApplyCoupon(coupon)}
                      disabled={isApplyingCoupon}
                      className="px-4 text-[#1E88E5] text-sm font-semibold uppercase h-9 flex items-center justify-center transition-all duration-500 hover:bg-[#DAEAFB] rounded flex-shrink-0 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isApplyingCoupon ? t('Applying...') : t('Apply')}
                    </button>
                  </div>

                  {/* Details */}
                  <div className="mt-2 text-xs text-[#666] space-y-1">
                    {coupon.minimumOrderValue > 0 && (
                      <p>{t('Minimum purchase: {{amount}}$', { amount: coupon.minimumOrderValue })}</p>
                    )}

                    {coupon.couponType.type === 'percentage' && coupon.couponType.maxAmount > 0 && (
                      <p>{t('Maximum discount: {{amount}}$', { amount: coupon.couponType.maxAmount })}</p>
                    )}

                    {coupon.restrictions?.firstOrderOnly && <p>{t('First order only')}</p>}

                    <p>{getScopeText(coupon)}</p>

                    <p>{t('Valid until {{date}}', { date: coupon.dateRange.endDate })}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center text-sm text-[#9DA0A7] py-8">
                  {t('No coupons found')}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40 transition-opacity duration-300 ${show ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default CouponModal;
